import React from 'react'
import { motion } from 'framer-motion'
import Reveal from '@/components/motion/Reveal'
import TiltCard from '@/components/motion/TiltCard'
import EditableText from '@/components/editable/EditableText'

const PILIERS = [
  {
    n: '01',
    t: 'Innovation',
    d: "Apprendre, expérimenter et créer avec l'IA.",
    icon: (
      <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M24 6 L24 12" strokeLinecap="round" />
        <path d="M18 36 L30 36 L30 38 Q30 42 26 42 L22 42 Q18 42 18 38 Z" />
        <path d="M16 24 Q16 14 24 14 Q32 14 32 24 Q32 30 28 32 L28 36 L20 36 L20 32 Q16 30 16 24 Z" />
        <path d="M8 22 L12 22 M36 22 L40 22 M11 11 L14 14 M34 14 L37 11" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    n: '02',
    t: 'Confiance',
    d: 'Reprendre confiance, oser entreprendre.',
    icon: (
      <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="16" cy="16" r="4" />
        <circle cx="32" cy="16" r="4" />
        <path d="M8 38 Q8 28 16 28 Q24 28 24 38" />
        <path d="M24 38 Q24 28 32 28 Q40 28 40 38" />
        <path d="M20 22 L24 18 L28 22" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    n: '03',
    t: 'Ambition',
    d: 'Construire son projet, atteindre ses objectifs.',
    icon: (
      <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M8 40 L24 8 L40 40 Z" strokeLinejoin="round" />
        <path d="M16 26 L24 18 L32 26" strokeLinejoin="round" />
        <path d="M24 8 L24 22 L32 18 Z" fill="currentColor" fillOpacity="0.2" />
      </svg>
    ),
  },
  {
    n: '04',
    t: 'Impact',
    d: 'Avoir un impact positif sur sa vie et son environnement.',
    icon: (
      <svg viewBox="0 0 48 48" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M24 42 L24 22" strokeLinecap="round" />
        <path d="M24 22 Q14 20 14 12 Q22 12 24 18 Q26 12 34 12 Q34 20 24 22" strokeLinejoin="round" />
        <path d="M16 42 L32 42" strokeLinecap="round" />
      </svg>
    ),
  },
]

const Piliers: React.FC = () => {
  return (
    <section id="piliers" className="relative bg-[#14253B] py-32 lg:py-44 overflow-hidden">
      {/* Subtle ambient glow */}
      <motion.div
        className="absolute top-1/2 right-0 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none -translate-y-1/2"
        style={{ background: 'radial-gradient(circle, rgba(184,194,107,0.08), transparent 70%)' }}
        animate={{ x: [0, 40, 0], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <Reveal className="lg:col-span-4">
            <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">
              <EditableText as="span" contentKey="piliers.section.kicker" section="Piliers" label="Numéro de section">02 — PILIERS</EditableText>
            </div>
            <motion.div
              className="h-px bg-gradient-to-r from-[#C8A24A] to-transparent mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 96 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            />
            <EditableText
              as="div"
              contentKey="piliers.eyebrow"
              section="Piliers"
              label="Piliers — sous-titre"
              className="text-[12px] tracking-mid uppercase text-[#C9BFA8]"
            >
              Quatre forces motrices
            </EditableText>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={0.15}>
            <EditableText
              as="h3"
              contentKey="piliers.title"
              section="Piliers"
              label="Piliers — titre principal"
              className="font-serif-display text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.05] text-[#E8DCC4] text-balance"
            >
              Chaque parcours s'appuie sur quatre piliers fondateurs.
            </EditableText>
            <EditableText
              as="p"
              multiline
              contentKey="piliers.intro"
              section="Piliers"
              label="Piliers — intro"
              className="mt-8 max-w-2xl text-[16px] leading-[1.75] text-[#C9BFA8]"
            >
              Les piliers du Centre ne sont pas théoriques. Ils sont conçus pour être vécus, expérimentés et appliqués directement dans des situations réelles.
            </EditableText>
          </Reveal>
        </div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
          }}
        >
          {PILIERS.map((p) => (
            <motion.div
              key={p.n}
              variants={{
                hidden: { opacity: 0, y: 40, filter: 'blur(10px)' },
                visible: {
                  opacity: 1,
                  y: 0,
                  filter: 'blur(0px)',
                  transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            >
              <TiltCard
                intensity={6}
                glare
                className="group relative p-8 hairline transition-colors duration-500 hover:border-[#C9E26A]/40 bg-[#0E1B2C]/30 backdrop-blur-sm rounded-[2px] h-full"
              >
                {/* Number watermark */}
                <div className="absolute top-0 right-0 text-[10px] tracking-luxe text-[#C8A24A]/60 p-4">{p.n}</div>

                {/* Big ghost number */}
                <motion.div
                  className="absolute -bottom-6 -right-2 font-serif-display text-[140px] leading-none text-[#C9E26A]/[0.04] select-none pointer-events-none"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, delay: 0.3 }}
                >
                  {p.n}
                </motion.div>

                <motion.div
                  className="text-[#C9E26A] mb-6 origin-left"
                  whileHover={{ scale: 1.12, rotate: -3 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {p.icon}
                </motion.div>
                <EditableText
                  as="div"
                  contentKey={`piliers.item.${p.n}.titre`}
                  section="Piliers"
                  label={`Pilier ${p.n} — titre`}
                  className="font-serif-display text-[28px] text-[#E8DCC4] mb-3"
                >
                  {p.t}
                </EditableText>
                <EditableText
                  as="p"
                  multiline
                  contentKey={`piliers.item.${p.n}.description`}
                  section="Piliers"
                  label={`Pilier ${p.n} — description`}
                  className="text-[14px] leading-[1.7] text-[#C9BFA8]"
                >
                  {p.d}
                </EditableText>
                <motion.div
                  className="mt-6 h-px bg-[#C8A24A]/40 group-hover:bg-[#C9E26A]"
                  initial={{ width: 32 }}
                  whileHover={{ width: 80 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </TiltCard>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Piliers
